import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "motion/react";
import { Switch } from "../ui/switch"
import { hasConsented, saveConsent, ConsentPrefs } from "../../../lib/cookies/consent"
import { applyConsentedCookies } from "../../../lib/cookies/setCookies"
import { Link } from "react-router"
import { ChevronDown, Cookie } from "lucide-react"

export function CookieBanner() {
  const [show, setShow] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Local state for the toggles before saving
  const [prefs, setPrefs] = useState({
    analytics: true,
    attribution: true,
  })

  useEffect(() => {
    // Only show if user has never consented before
    if (!hasConsented()) {
      setShow(true)
    }
  }, [])

  const handleSave = (finalPrefs: Omit<ConsentPrefs, "essential" | "timestamp">) => {
    saveConsent(finalPrefs)
    applyConsentedCookies({ essential: true, ...finalPrefs, timestamp: new Date().toISOString() })
    setShow(false)
  }

  const handleAcceptAll = () => handleSave({ analytics: true, attribution: true })
  const handleEssentialOnly = () => handleSave({ analytics: false, attribution: false })
  const handleSavePreferences = () => handleSave(prefs)

  if (!show) return null

  return (
    <div className="fixed bottom-0 inset-x-0 z-[9999] p-4 pointer-events-none flex justify-center">
      <AnimatePresence mode="wait">
        {!expanded ? (
          // MINIMAL VARIANT
          <motion.div
            key="minimal"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20, transition: { duration: 0.2 } }}
            className="pointer-events-auto bg-[#1A1A1A] text-white rounded-2xl shadow-2xl border border-white/10 p-4 max-w-4xl w-full"
          >
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-white/80">
                We use cookies to improve MoniFlow.{" "}
                <Link to="/privacy#cookies" className="text-[#34D399] hover:underline font-medium">
                  Learn more.
                </Link>
              </div>
              <div className="flex items-center justify-center gap-3 w-full sm:w-auto">
                <button
                  onClick={() => setExpanded(true)}
                  className="px-4 py-2 rounded-xl border border-white/20 text-white/90 text-sm font-semibold hover:bg-white/5 transition-colors shrink-0"
                >
                  Manage
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="px-6 py-2 rounded-xl bg-white text-black text-sm font-semibold hover:bg-gray-200 transition-colors shrink-0"
                >
                  OK
                </button>
              </div>
            </div>
            {/* Expand hit area indicating you can drag up / expand */}
            <div 
              className="absolute -top-6 left-1/2 -translate-x-1/2 cursor-pointer bg-[#1A1A1A] border border-white/10 rounded-full w-8 h-8 flex items-center justify-center shadow-lg"
              onClick={() => setExpanded(true)}
            >
              <ChevronDown className="w-4 h-4 text-white/60 rotate-180" />
            </div>
          </motion.div>
        ) : (
          // EXPANDED VARIANT
          <motion.div
            key="expanded"
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="pointer-events-auto bg-[#1A1A1A] text-white rounded-3xl shadow-2xl border border-white/10 p-5 md:p-8 max-w-4xl w-full"
          >
            {/* Header */}
            <div className="flex items-start gap-4 mb-8">
              <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
                <span className="text-2xl">🍪</span>
              </div>
              <div>
                <h2 className="text-xl font-bold mb-2">We use cookies on MoniFlow</h2>
                <p className="text-sm text-white/70 leading-relaxed">
                  We use essential cookies to keep the platform running and optional analytics cookies to understand how sellers use MoniFlow — so we can make it better.{" "}
                  <Link to="/privacy#cookies" className="text-[#34D399] hover:underline">
                    Read our cookie policy.
                  </Link>
                </p>
              </div>
            </div>

            {/* Toggles */}
            <div className="space-y-6 mb-8">
              {/* Essential */}
              <div className="flex items-start justify-between gap-4 pb-6 border-b border-white/10">
                <div>
                  <h3 className="font-semibold mb-1">Essential cookies</h3>
                  <p className="text-sm text-white/60">Required for login, sessions, and core features. Cannot be disabled.</p>
                </div>
                <Switch checked={true} disabled className="data-[state=checked]:bg-[#0B6E4F] shrink-0" />
              </div>
              
              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 pb-6 border-b border-white/10">
                <div>
                  <h3 className="font-semibold mb-1">Analytics cookies</h3>
                  <p className="text-sm text-white/60">Help us understand feature usage and improve the product.</p>
                </div>
                <Switch 
                  checked={prefs.analytics}
                  onCheckedChange={(c) => setPrefs(p => ({ ...p, analytics: c }))}
                  className="data-[state=checked]:bg-[#34D399] shrink-0" 
                />
              </div>

              {/* Attribution */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold mb-1">Attribution cookies</h3>
                  <p className="text-sm text-white/60">Track which channel brought you to MoniFlow — WhatsApp, Instagram, or direct.</p>
                </div>
                <Switch 
                  checked={prefs.attribution}
                  onCheckedChange={(c) => setPrefs(p => ({ ...p, attribution: c }))}
                  className="data-[state=checked]:bg-[#34D399] shrink-0" 
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center gap-3 relative">
              <button
                onClick={handleAcceptAll}
                className="w-full sm:flex-1 py-4 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Accept all
              </button>
              
              {/* Down Arrow / Collapse button */}
              <button 
                onClick={() => setExpanded(false)}
                className="hidden sm:flex absolute left-1/2 -translate-x-1/2 -bottom-4 w-10 h-10 bg-[#1A1A1A] border border-white/10 rounded-full items-center justify-center hover:bg-white/5 transition-colors z-10"
              >
                <ChevronDown className="w-5 h-5 text-white/60" />
              </button>

              <button
                onClick={handleSavePreferences}
                className="w-full sm:flex-1 py-4 rounded-xl border border-white/20 text-white font-semibold hover:bg-white/5 transition-colors"
              >
                Save my preferences
              </button>
              <button
                onClick={handleEssentialOnly}
                className="w-full sm:flex-1 py-4 rounded-xl bg-transparent border border-transparent text-white/80 font-semibold hover:text-white transition-colors underline decoration-white/30 underline-offset-4"
              >
                Essential only
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
